import { api } from "@/lib/api";
import type {
  Invitation,
  PublicInvitation,
  CreateInvitationDto,
  UpdateInvitationDto,
  SendInvitationEmailDto,
  AddGuestViaInvitationDto,
} from "@/types/invitation";
import type { Guest } from "@/types/guest";

class InvitationService {
  /**
   * Get all invitations for an event
   */
  async getByEvent(eventId: string): Promise<Invitation[]> {
    return api.get<Invitation[]>(`/invitations?eventId=${eventId}`);
  }

  /**
   * Get a single invitation by ID
   */
  async getById(id: string): Promise<Invitation> {
    return api.get<Invitation>(`/invitations/${id}`);
  }

  /**
   * Create a new invitation
   */
  async create(data: CreateInvitationDto): Promise<Invitation> {
    return api.post<Invitation>("/invitations", data);
  }

  /**
   * Update an invitation
   */
  async update(id: string, data: UpdateInvitationDto): Promise<Invitation> {
    return api.put<Invitation>(`/invitations/${id}`, data);
  }

  /**
   * Delete an invitation
   */
  async delete(id: string): Promise<Invitation> {
    return api.delete<Invitation>(`/invitations/${id}`);
  }

  /**
   * Send invitation email
   */
  async sendEmail(id: string, data: SendInvitationEmailDto): Promise<{ message: string }> {
    return api.post<{ message: string }>(`/invitations/${id}/send-email`, data);
  }

  // ============================================
  // PUBLIC (TOKEN-BASED) ENDPOINTS
  // ============================================

  /**
   * Get invitation by token (public)
   */
  async getByToken(token: string): Promise<PublicInvitation> {
    return api.get<PublicInvitation>(`/invitations/public/${token}`);
  }

  /**
   * Add guests via invitation token (public)
   */
  async addGuestsViaToken(token: string, data: AddGuestViaInvitationDto): Promise<Guest[]> {
    return api.post<Guest[]>(`/invitations/public/${token}/guests`, data);
  }

  /**
   * Bu davetiyeden eklenmiş misafirler (public, kişiye özel davetiye)
   */
  async getMyGuestsViaToken(token: string): Promise<Guest[]> {
    return api.get<Guest[]>(`/invitations/public/${token}/guests`);
  }

  /**
   * Update a guest via token (public)
   */
  async updateGuestViaToken(
    token: string,
    guestId: string,
    data: { name: string }
  ): Promise<Guest> {
    return api.put<Guest>(`/invitations/public/${token}/guests/${guestId}`, data);
  }

  /**
   * Delete a guest via token (public)
   */
  async deleteGuestViaToken(token: string, guestId: string): Promise<{ message: string }> {
    return api.delete<{ message: string }>(`/invitations/public/${token}/guests/${guestId}`);
  }
}

export const invitationService = new InvitationService();
